import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ButtonClickEvent } from 'ui/base/types';

import { SearchInput } from 'ui/standard/input/search_input/search_input';

import { DeviceType } from 'services/pangolin/types';

import { getDeviceTypeString } from 'pages/devices/view_device/view_device';

import {
  themedDeviceTypeIconClassStyles,
  getDeviceTypeIcon,
} from 'assets/enums/device_types/device_types';

import { DeviceTypeInputStore } from './device_type_input_store';
import { DeviceTypeList } from './device_type_input_data';

import baseStyles from './device_type_input.module.css';
import Messages from './device_type_input_messages';

type DeviceTypeInputStyles = {
  deviceTypeContainer: string;
  deviceIconContainer: string;
  deviceIcon: string;
  deviceNameContainer: string;
  inputButton: string;
  inputTypeContainer: string;
  inputTypeText: string;
  placeholder: string;
  removeIcon: string;
};

const themedStyles: ThemedStyles<DeviceTypeInputStyles> = {
  [Theme.DAYLIGHT]: {
    deviceTypeContainer: baseStyles.deviceTypeContainer,
    deviceIconContainer: baseStyles.deviceIconContainer,
    deviceIcon: baseStyles.deviceIcon,
    deviceNameContainer: baseStyles.deviceNameContainer,
    inputButton: baseStyles.daylightInputButton,
    inputTypeContainer: baseStyles.inputTypeContainer,
    inputTypeText: baseStyles.inputTypeText,
    placeholder: baseStyles.daylightPlaceholder,
    removeIcon: baseStyles.daylightRemoveIcon,
  },
  [Theme.MIDNIGHT]: {
    deviceTypeContainer: baseStyles.deviceTypeContainer,
    deviceIconContainer: baseStyles.deviceIconContainer,
    deviceIcon: baseStyles.deviceIcon,
    deviceNameContainer: baseStyles.deviceNameContainer,
    inputButton: baseStyles.midnightInputButton,
    inputTypeContainer: baseStyles.inputTypeContainer,
    inputTypeText: baseStyles.inputTypeText,
    placeholder: baseStyles.midnightPlaceholder,
    removeIcon: baseStyles.midnightRemoveIcon,
  },
};

type TypeInputDisplayProps = {
  store: DeviceTypeInputStore;
};

const TypeInputDisplay = observer(({ store }: TypeInputDisplayProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.search.modal.showModal();
  }, [store]);

  const onRemoveType = React.useCallback(action((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    store.removeType();
  }), [store]);

  if (store.deviceType != null) {
    return (
      <button type='button' className={styles.inputButton} onClick={onShowModal}>
        <div className={styles.inputTypeContainer}>
          <div className={styles.inputTypeText}>
            {getDeviceTypeString(store.deviceType)}
          </div>
          <div onClick={onRemoveType}>
            <div className={styles.removeIcon}/>
          </div>
        </div>
      </button>
    );
  } else {
    return (
      <button type='button' className={styles.inputButton} onClick={onShowModal}>
        <span className={styles.placeholder}>{Messages.placeholder()}</span>
      </button>
    );
  }
});

type DeviceTypeItemProps = {
  data: DeviceType;
  store: DeviceTypeInputStore;
  key: string;
};

const DeviceTypeItem = ({ data, store }: DeviceTypeItemProps) => {
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);
  const deviceIconClass = getDeviceTypeIcon(deviceIcons, data);

  const onClick = React.useCallback(action(() => {
    store.commitType(data);
    store.search.modal.hideModal();
  }), [store, data]);

  return (
    <div className={styles.deviceTypeContainer} onClick={onClick}>
      <div className={styles.deviceIconContainer}>
        <div className={classNames(styles.deviceIcon, deviceIconClass)}/>
      </div>
      <div className={styles.deviceNameContainer}>
        {getDeviceTypeString(data)}
      </div>
    </div>
  );
};

const filterTypes = (item: DeviceType, text: string) => {
  if (text) {
    const name = getDeviceTypeString(item);
    if (name) {
      return name.toLocaleLowerCase().indexOf(text) >= 0;
    }
    return false;
  }
  return true;
};

type DeviceTypeInputProps = {
  store: DeviceTypeInputStore;
};

export const DeviceTypeInput = observer(({ store }: DeviceTypeInputProps) => {
  const Display = React.useCallback(() => (
    <TypeInputDisplay store={store}/>
  ), [store]);
  const ItemComponent = (props: Omit<DeviceTypeItemProps, 'store'>) => {
    return <DeviceTypeItem store={store} {...props}/>;
  };
  return (
    <SearchInput
      placeholder={Messages.search()}
      store={store.search}
      data={DeviceTypeList}
      filterFunc={filterTypes}
      keyFunc={item => item.toString()}
      ItemComponent={ItemComponent}
      DisplayComponent={Display}
    />
  );
});
